import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import attendanceServices from "../../../services/attendanceServices";
import {
  getClubByUser,
  getClubById,
  getClubByTeacher,
} from "../../../services/clubServices";
import { getAdherentsByGroup } from "../../../services/adherentServices";
import {
  getDojosByClub,
  getDojosByTeacher,
} from "../../../services/dojoServices";
import {
  getGroupsByDojo,
  getGroupsByTeacher,
} from "../../../services/groupServices";
import { getTeacherByUser } from "../../../services/teacherServices";
import { useNotification } from "../../../components/Notification/NotificationManager";
import { isDemoUser } from "../../../utils/isDemoUser";
import useScrollToTop from "../../../hooks/useScollToTop";

import AdherentInfoModal from "./AdherentInfoModal";
import CreateAdherentModal from "../ManageMembers/CreateAdherentModal";
import AttendancePageHeader from "./components/AttendancePageHeader";
import AttendanceFiltersBar from "./components/AttendanceFiltersBar";
import AttendanceMembersSection from "./components/AttendanceMembersSection";

import {
  buildDojoGroupsMap,
  formatDisplayDate,
  getGroupDojoId,
  getTodayInputValue,
  normalizeAttendancePayload,
  shiftDateInputValue,
} from "./utils/attendancePageUtils";

import { FaArrowRight, FaUsers } from "react-icons/fa";

const AttendancePage = () => {
  useScrollToTop();

  const navigate = useNavigate();
  const { addNotification } = useNotification();

  let userInfo = null;
  try {
    userInfo = JSON.parse(localStorage.getItem("userInfo") || "null");
  } catch (error) {
    userInfo = null;
  }

  const userId = userInfo?.userId;
  const roleId = Number(userInfo?.role_id || 0);
  const demoMode = isDemoUser(userInfo);
  const isTeacher = roleId === 6;

  const [club, setClub] = useState(null);
  const [clubId, setClubId] = useState(null);

  const [teacher, setTeacher] = useState(null);
  const [teacherId, setTeacherId] = useState(null);

  const [dojos, setDojos] = useState([]);
  const [groups, setGroups] = useState([]);
  const [teacherAccessMap, setTeacherAccessMap] = useState({});

  const [adherents, setAdherents] = useState([]);
  const [attendance, setAttendance] = useState({});
  const [savingAttendanceMap, setSavingAttendanceMap] = useState({});

  const [selectedDojo, setSelectedDojo] = useState("");
  const [selectedGroup, setSelectedGroup] = useState("");
  const [attendanceDate, setAttendanceDate] = useState(getTodayInputValue());
  const [searchTerm, setSearchTerm] = useState("");

  const [loadingAccess, setLoadingAccess] = useState(true);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [pageError, setPageError] = useState("");

  const [infoModalOpen, setInfoModalOpen] = useState(false);
  const [selectedAdherent, setSelectedAdherent] = useState(null);
  const [createModalOpen, setCreateModalOpen] = useState(false);

  useEffect(() => {
    if (userId) {
      fetchInitialData();
    } else {
      setLoadingAccess(false);
    }
  }, [userId, roleId]);

  const fetchInitialData = async () => {
    setLoadingAccess(true);
    setPageError("");

    try {
      let resolvedClub = null;
      let resolvedTeacher = null;
      let resolvedDojos = [];
      let resolvedGroups = [];

      if (demoMode) {
        resolvedClub = await getClubById(2);
        resolvedDojos = (await getDojosByClub(2)) || [];
        resolvedGroups = (
          await Promise.all(
            resolvedDojos.map((dojo) => getGroupsByDojo(dojo.id)),
          )
        ).flat();
      } else if (isTeacher) {
        resolvedTeacher = await getTeacherByUser(userId);

        if (resolvedTeacher?.id) {
          resolvedClub = await getClubByTeacher(resolvedTeacher.id);
          resolvedDojos = (await getDojosByTeacher(resolvedTeacher.id)) || [];
          resolvedGroups = (await getGroupsByTeacher(resolvedTeacher.id)) || [];
        }
      } else {
        resolvedClub = await getClubByUser(userId);

        if (resolvedClub?.id) {
          resolvedDojos = (await getDojosByClub(resolvedClub.id)) || [];
          resolvedGroups = (
            await Promise.all(
              resolvedDojos.map((dojo) => getGroupsByDojo(dojo.id)),
            )
          ).flat();
        }
      }

      setClub(resolvedClub || null);
      setClubId(resolvedClub?.id || null);
      setTeacher(resolvedTeacher || null);
      setTeacherId(resolvedTeacher?.id || null);
      setDojos(Array.isArray(resolvedDojos) ? resolvedDojos : []);
      setGroups(Array.isArray(resolvedGroups) ? resolvedGroups : []);
      setTeacherAccessMap(
        buildDojoGroupsMap(
          Array.isArray(resolvedDojos) ? resolvedDojos : [],
          Array.isArray(resolvedGroups) ? resolvedGroups : [],
        ),
      );
    } catch (error) {
      console.error("Erreur lors du chargement des accès :", error);
      setPageError("Erreur lors du chargement des accès.");
      setClub(null);
      setClubId(null);
      setTeacher(null);
      setTeacherId(null);
      setDojos([]);
      setGroups([]);
      setTeacherAccessMap({});
    } finally {
      setLoadingAccess(false);
    }
  };

  const filteredGroups = useMemo(() => {
    if (!selectedDojo) return [];
    return groups.filter(
      (group) => getGroupDojoId(group) === String(selectedDojo),
    );
  }, [groups, selectedDojo]);

  const selectedGroupData = useMemo(() => {
    return (
      groups.find((group) => String(group.id) === String(selectedGroup)) || null
    );
  }, [groups, selectedGroup]);

  useEffect(() => {
    if (dojos.length === 1 && !selectedDojo) {
      setSelectedDojo(String(dojos[0].id));
    }
  }, [dojos, selectedDojo]);

  useEffect(() => {
    if (!selectedDojo) {
      setSelectedGroup("");
      return;
    }

    const hasCurrentGroup = filteredGroups.some(
      (group) => String(group.id) === String(selectedGroup),
    );

    if (!hasCurrentGroup) {
      if (filteredGroups.length === 1) {
        setSelectedGroup(String(filteredGroups[0].id));
      } else {
        setSelectedGroup("");
      }
    }
  }, [selectedDojo, filteredGroups, selectedGroup]);

  useEffect(() => {
    if (!selectedGroup || !attendanceDate) {
      setAdherents([]);
      setAttendance({});
      return;
    }

    fetchGroupSessionData(selectedGroup, attendanceDate);
  }, [selectedGroup, attendanceDate]);

  const fetchGroupSessionData = async (groupId, dateValue) => {
    if (!groupId || !dateValue) return [];

    try {
      setLoadingMembers(true);

      const [adherentsData, rawAttendance] = await Promise.all([
        getAdherentsByGroup(groupId),
        attendanceServices.getGroupAttendance(groupId, dateValue),
      ]);

      const safeAdherents = Array.isArray(adherentsData) ? adherentsData : [];
      const normalizedAttendance = normalizeAttendancePayload(rawAttendance);

      const mergedAttendance = safeAdherents.reduce((acc, adherent) => {
        acc[adherent.id] = normalizedAttendance[String(adherent.id)] || "";
        return acc;
      }, {});

      setAdherents(safeAdherents);
      setAttendance(mergedAttendance);

      return safeAdherents;
    } catch (error) {
      console.error("Erreur lors du chargement du groupe :", error);
      setAdherents([]);
      setAttendance({});
      addNotification("Erreur lors du chargement des présences.", "error");
      return [];
    } finally {
      setLoadingMembers(false);
    }
  };

  const handleAdherentInfoUpdate = async (updatedAdherent = null) => {
    const refreshedAdherents = await fetchGroupSessionData(
      selectedGroup,
      attendanceDate,
    );

    if (!updatedAdherent) return;

    const freshSelected = refreshedAdherents.find(
      (item) => String(item.id) === String(updatedAdherent.id),
    );

    // Si l'adhérent ne fait plus partie du groupe actuellement affiché
    if (!freshSelected) {
      setSelectedAdherent(null);
      setInfoModalOpen(false);
      return;
    }

    setSelectedAdherent({
      ...freshSelected,
      Groups:
        Array.isArray(updatedAdherent?.Groups) &&
        updatedAdherent.Groups.length > 0
          ? updatedAdherent.Groups
          : freshSelected?.Groups || [],
    });
  };

  const filteredAdherents = useMemo(() => {
    const search = searchTerm.trim().toLowerCase();

    return adherents.filter((adherent) => {
      if (!search) return true;

      const blob = [
        adherent?.nom || "",
        adherent?.prenom || "",
        adherent?.email || "",
        adherent?.tel || "",
      ]
        .join(" ")
        .toLowerCase();

      return blob.includes(search);
    });
  }, [adherents, searchTerm]);

  const attendanceSummary = useMemo(() => {
    return filteredAdherents.reduce(
      (acc, adherent) => {
        const status = attendance[adherent.id];

        acc.total += 1;

        if (status === "Présent") acc.present += 1;
        else if (status === "Absent") acc.absent += 1;
        else if (status === "Malade/Blessé") acc.sick += 1;
        else acc.pending += 1;

        return acc;
      },
      {
        total: 0,
        present: 0,
        absent: 0,
        sick: 0,
        pending: 0,
      },
    );
  }, [filteredAdherents, attendance]);

  const updateAttendanceStatus = async (adherentId, status) => {
    if (!selectedGroup || !attendanceDate || !adherentId || !status) return;

    const previousValue = attendance[adherentId] || "";

    setAttendance((prev) => ({
      ...prev,
      [adherentId]: status,
    }));

    setSavingAttendanceMap((prev) => ({
      ...prev,
      [adherentId]: true,
    }));

    try {
      try {
        await attendanceServices.updateAttendance(
          selectedGroup,
          attendanceDate,
          {
            adherent_id: adherentId,
            presence: status,
          },
        );
      } catch (updateError) {
        await attendanceServices.addAttendance({
          group_id: Number(selectedGroup),
          date_presence: attendanceDate,
          adherent_id: adherentId,
          presence: status,
        });
      }
    } catch (error) {
      console.error("Erreur lors de l'enregistrement de la présence :", error);

      setAttendance((prev) => ({
        ...prev,
        [adherentId]: previousValue,
      }));

      addNotification(
        "Erreur lors de l'enregistrement de la présence.",
        "error",
      );
    } finally {
      setSavingAttendanceMap((prev) => {
        const next = { ...prev };
        delete next[adherentId];
        return next;
      });
    }
  };

  const handleOpenInfo = (adherent) => {
    setSelectedAdherent(adherent);
    setInfoModalOpen(true);
  };

  const handleResetFilters = () => {
    setSelectedDojo("");
    setSelectedGroup("");
    setSearchTerm("");
    setAttendanceDate(getTodayInputValue());
  };

  const canCreateAdherent = Boolean(selectedDojo && selectedGroup && clubId);

  return (
    <div className="w-full bg-slate-50 pb-12 pt-[100px] dark:bg-slate-950">
      <div className="mx-auto max-w-[1280px] px-4 sm:px-6">
        {/* Header */}
        <section className="mt-4 rounded-[28px] border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900 sm:p-6 lg:p-8">
          <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <p className="text-sm font-semibold uppercase tracking-wider text-blue-600 dark:text-blue-400">
                Suivi de présence
              </p>
              <h1 className="mt-1 text-3xl font-black text-slate-900 dark:text-slate-100">
                Appel des groupes
              </h1>
              <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600 dark:text-slate-400">
                Sélectionne rapidement un lieu, un groupe et une date, puis fais
                l’appel dans une interface plus fluide et plus lisible.
              </p>
            </div>

            {demoMode && (
              <span className="inline-flex rounded-full bg-amber-100 px-4 py-2 text-sm font-semibold text-amber-700 dark:bg-amber-500/10 dark:text-amber-300">
                Mode Démo
              </span>
            )}
          </div>
        </section>

        {pageError ? (
          <div className="mt-5 rounded-[24px] border border-red-200 bg-red-50 px-5 py-4 text-sm text-red-700 dark:border-red-500/20 dark:bg-red-500/10 dark:text-red-300">
            {pageError}
          </div>
        ) : null}

        {loadingAccess ? (
          <AttendancePageHeader
            loading
            club={null}
            dojos={[]}
            groups={[]}
            adherentsCount={0}
            attendanceDateLabel=""
            isTeacher={isTeacher}
            teacher={teacher}
            teacherAccessMap={teacherAccessMap}
          />
        ) : !club ? (
          <section className="mt-5 rounded-[28px] border border-slate-200 bg-white p-8 text-center shadow-sm dark:border-slate-800 dark:bg-slate-900">
            <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-300">
              <FaUsers className="text-4xl" />
            </div>

            <h2 className="mt-5 text-2xl font-black text-slate-900 dark:text-slate-100">
              Aucun club trouvé
            </h2>
            <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-400">
              Crée d’abord ton club avant de gérer les présences.
            </p>

            <button
              onClick={() => navigate("/votre-club")}
              className="mt-6 inline-flex items-center justify-center gap-2 rounded-2xl bg-blue-600 px-5 py-3 font-semibold text-white transition hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600"
            >
              Aller à votre club
              <FaArrowRight />
            </button>
          </section>
        ) : (
          <>
            <AttendancePageHeader
              loading={false}
              club={club}
              dojos={dojos}
              groups={groups}
              adherentsCount={adherents.length}
              attendanceDateLabel={formatDisplayDate(attendanceDate)}
              isTeacher={isTeacher}
              teacher={teacher}
              teacherAccessMap={teacherAccessMap}
            />

            <AttendanceFiltersBar
              dojos={dojos}
              filteredGroups={filteredGroups}
              selectedDojo={selectedDojo}
              selectedGroup={selectedGroup}
              attendanceDate={attendanceDate}
              onChangeDojo={(value) => {
                setSelectedDojo(value);
                setSelectedGroup("");
                setSearchTerm("");
              }}
              onChangeGroup={(value) => {
                setSelectedGroup(value);
                setSearchTerm("");
              }}
              onChangeDate={setAttendanceDate}
              onPreviousDay={() =>
                setAttendanceDate((prev) => shiftDateInputValue(prev, -1))
              }
              onNextDay={() =>
                setAttendanceDate((prev) => shiftDateInputValue(prev, 1))
              }
              onReset={handleResetFilters}
              disabled={loadingAccess}
            />

            <AttendanceMembersSection
              selectedDojo={selectedDojo}
              selectedGroup={selectedGroup}
              selectedGroupData={selectedGroupData}
              loadingMembers={loadingMembers}
              searchTerm={searchTerm}
              onSearchChange={setSearchTerm}
              adherents={adherents}
              filteredAdherents={filteredAdherents}
              attendance={attendance}
              attendanceSummary={attendanceSummary}
              savingAttendanceMap={savingAttendanceMap}
              onOpenInfo={handleOpenInfo}
              onUpdateAttendance={updateAttendanceStatus}
              onOpenCreateAdherent={() => setCreateModalOpen(true)}
              canCreateAdherent={canCreateAdherent}
            />
          </>
        )}
      </div>

      <AdherentInfoModal
        isOpen={infoModalOpen}
        onClose={() => setInfoModalOpen(false)}
        adherent={selectedAdherent}
        onUpdate={handleAdherentInfoUpdate}
        adherentGroups={selectedAdherent?.Groups || []}
        clubId={clubId}
      />

      <CreateAdherentModal
        isOpen={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onUpdate={() => fetchGroupSessionData(selectedGroup, attendanceDate)}
        clubId={clubId}
        dojoId={selectedDojo}
        groupId={selectedGroup}
      />
    </div>
  );
};

export default AttendancePage;
